'use client';
// import { useState, useEffect } from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { Conversation } from '@/types';
import ShareModal from './ShareModal';
import SidebarHeader from './Sidebar/SidebarHeader';
import NavigationMenu from './Sidebar/NavigationMenu';
import ProjectList from './Sidebar/ProjectList';
import ConversationList from './Sidebar/ConversationList';
import UserSection from './Sidebar/UserSection';
import { fetchGroupConversations } from '@/services/groupChatService';
import { LogIn, MoreHorizontal, ChevronDown, ChevronRight } from 'lucide-react';
import { useProjectsStore, useUIStore, useConversationsStore } from '@/stores';
import { useConversationsContext } from '@/contexts/ConversationsContext';
import { useConversationActions } from '@/hooks/conversation/useConversationActions';
import { useProjects } from '@/hooks/project/useProjects';
import { apiGet, apiPost } from '@/lib/api';
import { toast } from '@/hooks/ui/use-toast';
// Extend Window interface to include our custom property
declare global {
  interface Window {
    refreshSidebarProjects?: () => void;
    refreshSidebarSharedCount?: () => void;
    refreshSidebarGroupChats?: () => Promise<void>;
    refreshSidebarStarred?: () => Promise<void>; // 🔥 刷新 starred 列表
    refreshSidebarRecent?: () => Promise<void>; // 🔥 刷新 recent chats 列表
    getProjectStarredConversations?: (
      projectId: string
    ) => Array<{ conversationId?: string; id?: string }>; // 🔥 获取指定项目的 starred conversations
    updateConversationStarred?: (
      conversationId: string,
      starred: boolean
    ) => void; // 🔥 更新 Recent chats 的 starred 状态
    setCurrentConversationStarred?: (
      conversationId: string,
      starred: boolean
    ) => void; // 🔥 更新 ChatInput 的 starred 状态
    groupChatIds?: Set<string>;
    groupConversations?: Conversation[];
  }
}
interface SidebarProps {
  conversations: Conversation[];
  projectConversations: Conversation[];
  currentConversationId?: string;
  isCollapsed: boolean;
  isLoadingGeneralConversations?: boolean;
  shareConversationId?: string;
  isLoggedIn?: boolean;
  selectedNavItem?: string;
  userInfo?: { name?: string; email?: string };
  currentProjectId?: string | null;
  onConversationSelect: (id: string, projectId?: string) => void;
  onNewConversation: () => void;
  onUpdateConversation: (
    id: string,
    method: string,
    title?: string
  ) => Promise<void>;
  onRenameConversation: (id: string, title: string) => void;
  onToggleCollapse: () => void;
  onShowAgentList: () => void;
  onShowSharedList: () => void;
  onShowGroupChats?: () => void;
  onBackToChat: () => void;
  onSelectedNavItem: (item: string) => void;
  onLogin: () => void;
  onLogout: () => void;
  onNewProject: () => void;
  onProjectSelect: (projectId: string | null) => void;
  onBackToGeneralChats: () => void;
  onProjectCreated?: () => void;
  onRenameProject: (projectId: string, newName: string) => void;
  // onUpdateProjectConversation: (projectId: string, method: 'DELETE') => void;
  onShowProjectsList: () => void;
}
export default function Sidebar({
  conversations,
  currentConversationId,
  isCollapsed: isCollapsedProp,
  isLoadingGeneralConversations,
  shareConversationId,
  isLoggedIn,
  selectedNavItem,
  userInfo,
  currentProjectId,
  onConversationSelect,
  onNewConversation,
  onUpdateConversation,
  onRenameConversation,
  onToggleCollapse,
  onShowAgentList,
  onShowSharedList,
  onShowGroupChats,
  onBackToChat,
  onSelectedNavItem,
  onLogin,
  onLogout,
  onNewProject,
  onProjectSelect,
  onBackToGeneralChats,
  onRenameProject,
  // onUpdateProjectConversation,
  onShowProjectsList,
}: SidebarProps) {
  const { t } = useTranslation();
  const [, setForceUpdate] = useState(0);

  // 监听语言变化
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(n => n + 1);
    };
    i18nInstance.on('languageChanged', handleLanguageChange);
    return () => {
      i18nInstance.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // Get projects from centralized store instead of local state
  const storeProjects = useProjectsStore(state => state.projects);
  const updateProject = useProjectsStore(state => state.updateProject);
  // 🔥 获取 loadProjects 用于刷新侧边栏项目列表
  const { loadProjects } = useProjects();
  // 🔥 获取 setFilePreview 用于在 New Chat 时关闭文件预览
  const setFilePreview = useUIStore(state => state.setFilePreview);
  // Calculate unvisited projects count (lastVisitedAt is null)
  const unvisitedProjectsCount = storeProjects.filter(
    p =>
      !p.archived && (p.lastVisitedAt === null || p.lastVisitedAt === undefined)
  ).length;
  // Get project conversations cache to check loading状态
  const { sidebarProjectConversationsCache } = useConversationsContext();

  // Starred expand state
  const [isStarredExpanded, setIsStarredExpanded] = useState(() => {
    const saved = localStorage.getItem('starredExpanded');
    return saved !== null ? saved === 'true' : true; // 默认展开
  });
  // Projects expand state
  const [isProjectsExpanded, setIsProjectsExpanded] = useState(false);
  // Group chats expand state
  const [isGroupChatsExpanded, setIsGroupChatsExpanded] = useState(false);
  // Group chats state
  const [groupConversations, setGroupConversations] = useState<Conversation[]>(
    []
  );
  const [groupChatsLoading, setGroupChatsLoading] = useState(false);
  // Recent chats local state (fetch the first page/all=true)
  const [recentConversations, setRecentConversations] = useState<
    Conversation[]
  >([]);
  const [recentTotalCount, setRecentTotalCount] = useState<number>(0);
  const [recentLoading, setRecentLoading] = useState<boolean>(false);
  // 🔥 Starred conversations local state (fetch with starred=true)
  const [starredConversations, setStarredConversations] = useState<
    Conversation[]
  >([]);
  const [showAllChatsButton, setShowAllChatsButton] = useState<boolean>(false);

  // 拖拽调整侧边栏宽度的状态
  const MIN_WIDTH = 64; // 最小宽度（收起状态）
  const MAX_WIDTH = 420; // 最大宽度
  const COLLAPSE_THRESHOLD = 130; // 小于此宽度时自动收起

  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    // 从 localStorage 读取用户保存的宽度
    const savedWidth = localStorage.getItem('sidebarWidth');
    return savedWidth
      ? parseInt(savedWidth, 10)
      : isCollapsedProp
        ? MIN_WIDTH
        : 288;
  });

  const [isDragging, setIsDragging] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    isCollapsedProp || sidebarWidth <= COLLAPSE_THRESHOLD
  );
  const sidebarRef = useRef<HTMLDivElement>(null);

  // 🔥 同步外部 isCollapsedProp 到本地 isCollapsed 状态
  // 修复移动端汉堡按钮切换侧边栏时，本地状态不更新导致内容为空的问题
  useEffect(() => {
    setIsCollapsed(isCollapsedProp);
    if (!isCollapsedProp && sidebarWidth <= COLLAPSE_THRESHOLD) {
      // 如果外部要求展开，但当前宽度太小，恢复默认宽度
      setSidebarWidth(288);
      localStorage.setItem('sidebarWidth', '288');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsedProp]);

  // 处理拖拽开始
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  // 处理拖拽移动
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      let newWidth = e.clientX;

      // 限制宽度范围
      newWidth = Math.max(MIN_WIDTH, Math.min(newWidth, MAX_WIDTH));

      setSidebarWidth(newWidth);

      // 自动收起/展开逻辑
      if (newWidth < COLLAPSE_THRESHOLD) {
        setIsCollapsed(true);
        setSidebarWidth(MIN_WIDTH);
      } else {
        setIsCollapsed(false);
      }
    },
    [isDragging]
  );

  // 处理拖拽结束
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      // 保存用户设置的宽度
      localStorage.setItem('sidebarWidth', sidebarWidth.toString());
    }
  }, [isDragging, sidebarWidth]);

  // 监听鼠标事件
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // 防止拖拽时选中文本
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // 处理收起/展开切换
  const handleToggleCollapse = useCallback(() => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    setSidebarWidth(newCollapsed ? MIN_WIDTH : 288);
    localStorage.setItem(
      'sidebarWidth',
      (newCollapsed ? MIN_WIDTH : 288).toString()
    );
    onToggleCollapse();
  }, [isCollapsed, onToggleCollapse]);

  // 派发侧边栏宽度变化事件，让其他组件（如 UserSettings）能够实时适应
  // 🔥 使用 requestAnimationFrame 节流，避免拖拽时每帧多次派发
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      const event = new CustomEvent('sidebarWidthChange', {
        detail: { width: sidebarWidth },
      });
      window.dispatchEvent(event);
    });
    return () => cancelAnimationFrame(rafId);
  }, [sidebarWidth]);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  // 🔥 新增：跟踪上次排序的会话ID顺序，避免流式传输时频繁跳动
  const lastSortedConversationIdsRef = useRef<string[]>([]);
  // Unvisited shared count for badge
  const [unvisitedSharedCount, setUnvisitedSharedCount] = useState<number>(0);
  // Share modal state
  const [shareModal, setShareModal] = useState<{
    isOpen: boolean;
    conversationId: string;
    conversationTitle: string;
    projectId?: string | null;
  }>({
    isOpen: false,
    conversationId: '',
    conversationTitle: '',
    projectId: null,
  });

  // Track which section the project was selected from ('starred' or 'projects')
  const [projectSelectedFrom, setProjectSelectedFrom] = useState<
    'starred' | 'projects' | null
  >(null);

  // Reset projectSelectedFrom when currentProjectId becomes null (navigating away from project)
  useEffect(() => {
    if (!currentProjectId) {
      setProjectSelectedFrom(null);
    }
  }, [currentProjectId]);

  // 🔥 Fetch recent conversations callback
  // silent=true 时不显示 loading 状态（用于 SSE 流结束后的无感刷新）
  const fetchRecentConversations = useCallback(
    async (silent = false) => {
      if (!isLoggedIn) return;
      const PAGE_SIZE = 30;
      if (!silent) setRecentLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('page', '1');
        params.append('pageSize', String(PAGE_SIZE));
        params.append('allProjects', 'true');
        params.append('starred', 'false'); // 🔥 只获取未 starred 的对话，避免与 Starred 列表重叠
        const res = await apiGet<any>(
          `/api/ai/conversations?${params.toString()}`
        );
        const data = res?.data || res || {};
        const items = Array.isArray(data.conversations)
          ? data.conversations
          : [];
        const mapped: Conversation[] = items.map((conv: any) => ({
          id: conv.conversationId,
          conversationId: conv.conversationId,
          title: conv.title,
          treeData: [],
          versionPath: {},
          createdAt: conv.createdAt || new Date().toISOString(),
          updatedAt:
            conv.updatedAt || conv.createdAt || new Date().toISOString(),
          projectId: conv.projectId || null,
          starred: conv.starred || false,
          conversationType: conv.conversationType || 'single',
        }));
        setRecentConversations(mapped);
        setRecentTotalCount(Number(data.totalCount || 0));

        // 🔥 同步更新全局 conversations store 中的 starred 属性为 false
        // 确保 Chat 组件获取的 currentConversation.starred 是正确的
        const { setConversations } = useConversationsStore.getState();
        const unstarredIds = new Set(
          mapped.map(c => String(c.conversationId || c.id))
        );
        setConversations(prev =>
          prev.map(conv => {
            const convId = String(conv.conversationId || conv.id);
            if (unstarredIds.has(convId)) {
              return { ...conv, starred: false };
            }
            return conv;
          })
        );
      } catch (err) {
        // ignore - sidebar should be resilient
      } finally {
        if (!silent) setRecentLoading(false);
      }
    },
    [isLoggedIn]
  );

  // Fetch recent conversations on mount
  useEffect(() => {
    fetchRecentConversations();
  }, [fetchRecentConversations]);

  // 🔥 Expose refreshSidebarRecent to window（默认静默刷新，无 loading 闪烁）
  useEffect(() => {
    window.refreshSidebarRecent = () => fetchRecentConversations(true);
    return () => {
      delete window.refreshSidebarRecent;
    };
  }, [fetchRecentConversations]);

  // 🔥 Fetch starred conversations callback (with starred=true parameter)
  // 这解决了 starred 对话超过 30 条时无法显示的问题
  const fetchStarredConversations = useCallback(async () => {
    if (!isLoggedIn) return;
    const PAGE_SIZE = 50; // 获取更多的 starred 对话
    try {
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('pageSize', String(PAGE_SIZE));
      params.append('allProjects', 'true');
      params.append('starred', 'true'); // 🔥 关键：只获取 starred 的对话
      const res = await apiGet<any>(
        `/api/ai/conversations?${params.toString()}`
      );
      const data = res?.data || res || {};
      const items = Array.isArray(data.conversations) ? data.conversations : [];
      const mapped: Conversation[] = items.map((conv: any) => ({
        id: conv.conversationId,
        conversationId: conv.conversationId,
        title: conv.title,
        treeData: [],
        versionPath: {},
        createdAt: conv.createdAt || new Date().toISOString(),
        updatedAt: conv.updatedAt || conv.createdAt || new Date().toISOString(),
        projectId: conv.projectId || null,
        starred: conv.starred || false,
        conversationType: conv.conversationType || 'single',
      }));
      setStarredConversations(mapped);

      // 🔥 同步更新全局 conversations store 中的 starred 属性
      // 确保 Chat 组件获取的 currentConversation.starred 是正确的
      const { setConversations } = useConversationsStore.getState();
      const starredIds = new Set(
        mapped.map(c => String(c.conversationId || c.id))
      );
      setConversations(prev =>
        prev.map(conv => {
          const convId = String(conv.conversationId || conv.id);
          if (starredIds.has(convId)) {
            return { ...conv, starred: true };
          }
          return conv;
        })
      );
    } catch (err) {
      // ignore - sidebar should be resilient
    }
  }, [isLoggedIn]);

  // Initial fetch of starred conversations
  useEffect(() => {
    fetchStarredConversations();
  }, [fetchStarredConversations]);

  // Expose fetchStarredConversations to window for external refresh (e.g., after star/unstar operation)
  useEffect(() => {
    window.refreshSidebarStarred = fetchStarredConversations;
    return () => {
      delete window.refreshSidebarStarred;
    };
  }, [fetchStarredConversations]);

  // 🔥 Expose loadProjects to window for external refresh (e.g., after quit project)
  useEffect(() => {
    window.refreshSidebarProjects = loadProjects;
    return () => {
      delete window.refreshSidebarProjects;
    };
  }, [loadProjects]);

  // 🔥 Expose function to get starred conversations for a specific project (for Archive operation)
  useEffect(() => {
    window.getProjectStarredConversations = (projectId: string) => {
      return starredConversations.filter(c => c.projectId === projectId);
    };
    return () => {
      delete window.getProjectStarredConversations;
    };
  }, [starredConversations]);

  // 🔥 Expose function to update conversation starred state (for external components like ChatInput)
  useEffect(() => {
    window.updateConversationStarred = (
      conversationId: string,
      starred: boolean
    ) => {
      setRecentConversations(prev =>
        prev.map(c =>
          (c.conversationId || c.id) === conversationId ? { ...c, starred } : c
        )
      );
    };
    return () => {
      delete window.updateConversationStarred;
    };
  }, []);

  // 🔥 Star/Unstar conversation handler
  const { toggleStar } = useConversationActions({});

  const handleToggleStar = useCallback(
    async (conversationId: string, currentStarred: boolean) => {
      try {
        const success = await toggleStar(conversationId, currentStarred);
        if (success) {
          // 🔥 刷新两个列表：Starred 和 Recent chats
          // 由于 Recent chats 现在使用 starred=false 参数，需要从服务器重新获取
          await Promise.all([
            fetchStarredConversations(),
            fetchRecentConversations(),
          ]);
          // 🔥 同步 ChatInput 的 starred 状态
          window.setCurrentConversationStarred?.(
            conversationId,
            !currentStarred
          );
          // 🔥 同步 ChatsPage 和项目详情页的 conversations 状态
          window.updateConversationStarInPages?.(
            conversationId,
            !currentStarred
          );
        }
      } catch (err) {
        console.error('Failed to toggle star:', err);
      }
    },
    [toggleStar, fetchStarredConversations, fetchRecentConversations]
  );

  // 🔥 处理项目取消收藏
  const handleUnstarProject = useCallback(
    async (projectId: string) => {
      try {
        const res = await apiPost(`/api/projects/${projectId}/star`, {
          starred: false,
        });
        // 🔥 检查后端响应码
        if (res?.code !== 200) {
          toast({
            title: t('common.error'),
            description: res?.message || t('project.updateFailed'),
            variant: 'destructive',
          });
          return;
        }
        // 更新 store 中的项目状态
        updateProject(projectId, { starred: false });
        toast({
          title: t('common.success'),
          description: t('chat.unstar'),
        });
      } catch (err) {
        console.error('Failed to unstar project:', err);
        toast({
          title: t('common.error'),
          description: t('project.updateFailed'),
          variant: 'destructive',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateProject]
  );

  // Fetch group conversations callback
  const fetchGroupChats = useCallback(async () => {
    if (!isLoggedIn) return;
    setGroupChatsLoading(true);
    try {
      const groups = await fetchGroupConversations(1, 30);
      setGroupConversations(groups);
      // Update global groupChatIds Set for checking in selectConversation
      window.groupChatIds = new Set(groups.map(g => g.id));
      // 🔥 同时保存完整的群聊数据，供 loadConversationDetails 使用
      window.groupConversations = groups;
    } catch (err) {
      // ignore - sidebar should be resilient
    } finally {
      setGroupChatsLoading(false);
    }
  }, [isLoggedIn]);

  // Initial fetch of group conversations
  useEffect(() => {
    fetchGroupChats();
  }, [fetchGroupChats]);

  // Expose refresh function to window for external calls
  useEffect(() => {
    window.refreshSidebarGroupChats = fetchGroupChats;
    return () => {
      delete window.refreshSidebarGroupChats;
    };
  }, [fetchGroupChats]);

  // Fetch unvisited shared count for badge
  const fetchSharedCount = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const res = await apiGet<any>('/api/ai/conversations/shared');
      const data = res?.data || res || {};
      const items: any[] = Array.isArray(data.sharedConversations)
        ? data.sharedConversations
        : [];
      // Count items where lastVisitedAt is null
      const unvisitedCount = items.filter(
        (it: any) =>
          it?.lastVisitedAt === null || it?.lastVisitedAt === undefined
      ).length;
      setUnvisitedSharedCount(unvisitedCount);
    } catch (err) {
      // ignore
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchSharedCount();
  }, [fetchSharedCount]);

  // Expose refresh function to window for external calls
  useEffect(() => {
    window.refreshSidebarSharedCount = fetchSharedCount;
    return () => {
      delete window.refreshSidebarSharedCount;
    };
  }, [fetchSharedCount]);

  // Keep recent conversations in sync when upstream conversations更新（例如删除/重命名后调用 fetchConversations）
  // 🔥 优化：在流式传输期间保持排序稳定，避免对话列表频繁跳动
  useEffect(() => {
    if (!conversations || conversations.length === 0) return;

    // 🔥 检查是否有任何对话正在流式传输（isLoading = true）
    const hasAnyStreaming = conversations.some(conv => conv.isLoading);

    // 获取之前的排序顺序
    const prevSortedIds = lastSortedConversationIdsRef.current;

    let sorted: Conversation[];

    // 🔥 如果有对话正在流式传输 且 有之前的排序顺序，则保持原有顺序
    if (hasAnyStreaming && prevSortedIds.length > 0) {
      // 使用之前的排序顺序，只更新会话数据
      const convMap = new Map(conversations.map(c => [c.id, c]));

      // 按之前的顺序重建列表
      sorted = prevSortedIds
        .map(id => convMap.get(id))
        .filter((c): c is Conversation => c !== undefined);

      // 添加任何新的会话到列表开头（可能是刚创建的新对话）
      const existingIds = new Set(prevSortedIds);
      const newConvs = conversations.filter(c => !existingIds.has(c.id));
      if (newConvs.length > 0) {
        sorted = [...newConvs, ...sorted];
      }
    } else {
      // 🔥 没有流式传输时，按更新时间倒序排序
      sorted = [...conversations].sort((a, b) => {
        const timeA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const timeB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return timeB - timeA;
      });
    }

    // 🔥 更新排序顺序的引用（仅在没有流式传输时更新，以便下次使用）
    if (!hasAnyStreaming) {
      lastSortedConversationIdsRef.current = sorted.map(c => c.id);
    }

    // 🔥 过滤掉 starred 的对话，保持与 fetchRecentConversations 的 starred=false 一致
    // 这样可以避免全局 store 更新后覆盖掉 API 返回的正确数据
    const filteredSorted = sorted.filter(c => !c.starred);

    // 🔥 保留原有的 conversationType 字段，避免被覆盖丢失
    setRecentConversations(prev => {
      const prevMap = new Map(
        prev.map(c => [c.conversationId || c.id, c.conversationType])
      );
      return filteredSorted.map(conv => ({
        ...conv,
        conversationType:
          conv.conversationType ||
          prevMap.get(conv.conversationId || conv.id) ||
          'single',
      }));
    });
    // 注意：不要覆盖 recentTotalCount，它应该保持从 API 返回的真实总数
    // setRecentTotalCount(sorted.length); // ❌ 移除：这会覆盖 API 返回的 totalCount
  }, [conversations]);

  // Check if any conversation is loading (including both general and project conversations)
  const hasLoadingGeneralConversations = conversations.some(
    conv => conv.isLoading
  );
  // Check if any project conversation is loading
  const hasLoadingProjectConversations = Object.values(
    sidebarProjectConversationsCache
  ).some(projectConvs => projectConvs.some(conv => conv.isLoading));
  const hasLoadingConversations =
    hasLoadingGeneralConversations || hasLoadingProjectConversations;

  // 🔥 Check if any RECENT (non-starred, non-archived) conversation is loading
  // 这确保 Starred conversation 的 loading 不会出现在 Recent chats 中
  const hasLoadingRecentConversations = conversations.some(conv => {
    if (!conv.isLoading || conv.starred) return false;
    // 检查是否在 archived 项目中
    if (conv.projectId) {
      const project = storeProjects.find(p => p.id === conv.projectId);
      if (project?.archived) return false;
    }
    return true;
  });

  // 🔥 Check if any STARRED conversation is loading
  // 用于在 Starred 标题后显示 "Generating..."
  const hasLoadingStarredConversations = conversations.some(
    conv => conv.isLoading && conv.starred
  );
  // Scroll handler: show All chats button when scrolled to bottom
  // 🔥 依赖 isCollapsed：当侧边栏从折叠切换到展开时，scrollContainerRef 才挂载到 DOM
  // 需要重新执行 effect 以绑定滚动监听器和触发初始检查
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
      setShowAllChatsButton(atBottom && recentTotalCount > 30);
    };
    el.addEventListener('scroll', onScroll);
    // initial check
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [recentTotalCount, isCollapsed]);
  const handleShareConversation = (conversation: Conversation) => {
    setShareModal({
      isOpen: true,
      conversationId: conversation.id,
      conversationTitle: conversation.title,
      projectId: conversation.projectId || null,
    });
  };
  const closeShareModal = () => {
    setShareModal({
      isOpen: false,
      conversationId: '',
      conversationTitle: '',
      projectId: null,
    });
  };
  // Collapsed view
  // 始终使用动态宽度渲染，不再根据 isCollapsed 返回不同的 JSX
  return (
    <div className="flex h-full">
      <div
        ref={sidebarRef}
        className={`bg-sidebar-bg border-r border-gray-200 flex flex-col h-full relative ${isDragging ? 'select-none' : 'transition-[width] duration-200 ease-in-out'} ${isCollapsed ? '' : 'flex-shrink-0 p-4'} `}
        style={{ width: `${isCollapsed ? MIN_WIDTH : sidebarWidth}px` }}
      >
        <SidebarHeader
          isCollapsed={isCollapsed}
          shareConversationId={shareConversationId}
          onToggleCollapse={handleToggleCollapse}
          onNewConversation={() => {
            setProjectSelectedFrom(null);
            setFilePreview(null); // 🔥 关闭文件预览
            onNewConversation();
          }}
          onSelectedNavItem={onSelectedNavItem}
          onBackToChat={onBackToChat}
        />
        {/* 移动端/平板隐藏导航菜单（改为底部 Tab Bar 显示） */}
        <div className="hidden lg:block">
          <NavigationMenu
            isCollapsed={isCollapsed}
            selectedNavItem={selectedNavItem || ''}
            currentProjectId={currentProjectId}
            projectSelectedFrom={projectSelectedFrom}
            onSelectedNavItem={onSelectedNavItem}
            onBackToChat={onBackToChat}
            onBackToGeneralChats={onBackToGeneralChats}
            onProjectSelect={onProjectSelect}
            onConversationSelect={onConversationSelect}
            onShowAgentList={onShowAgentList}
            onShowSharedList={onShowSharedList}
            onNewProject={onNewProject}
            onShowProjectsList={onShowProjectsList}
            onSetProjectSelectedFrom={setProjectSelectedFrom}
            onToggleProjectsExpand={() =>
              setIsProjectsExpanded(!isProjectsExpanded)
            }
            isProjectsExpanded={isProjectsExpanded}
            unvisitedProjectsCount={unvisitedProjectsCount}
            unvisitedSharedCount={unvisitedSharedCount}
            // Group chats props
            isGroupChatsExpanded={isGroupChatsExpanded}
            onToggleGroupChatsExpand={() =>
              setIsGroupChatsExpanded(!isGroupChatsExpanded)
            }
            onShowGroupChats={onShowGroupChats}
            groupChatsCount={
              groupConversations.filter(
                g => g.lastVisitedAt === null || g.lastVisitedAt === undefined
              ).length
            }
            projects={storeProjects}
            groupConversations={groupConversations}
            groupChatsLoading={groupChatsLoading}
            onGroupChatSelect={id => {
              onConversationSelect(id);
              onSelectedNavItem('groupChats');
            }}
          />
        </div>
        {/* 根据收起/展开状态显示不同内容 */}
        {isCollapsed ? (
          <>
            <div className="flex-1"></div>
            {/* 收起状态的用户区域 */}
            <div className="p-3 border-t border-gray-200">
              {isLoggedIn ? (
                <div className="flex justify-center">
                  <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">
                      {(userInfo?.name || userInfo?.email || 'User')
                        .substring(0, 2)
                        .toUpperCase()}
                    </span>
                  </div>
                </div>
              ) : (
                <button
                  onClick={onLogin}
                  className="w-full flex justify-center p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Login"
                >
                  <LogIn size={16} />
                </button>
              )}
            </div>
          </>
        ) : (
          /* 展开状态的内容 */
          <>
            <div
              ref={scrollContainerRef}
              className={`flex-1 overflow-y-auto overflow-x-hidden min-h-0 ${isDragging ? 'pointer-events-none' : ''}`}
            >
              {/* Starred Section */}
              {!shareConversationId &&
                (() => {
                  const starredProjects = storeProjects.filter(
                    p => p.starred && !p.archived
                  );
                  // 🔥 始终使用 API 数据（starredConversations）作为唯一数据源
                  // 不再使用 fallback（conversations/cache），避免 stale 数据导致 star/unstar 后列表不更新
                  const allStarredConversations = starredConversations;
                  const uniqueStarredConversations = Array.from(
                    new Map(
                      allStarredConversations.map(c => [c.id, c])
                    ).values()
                  );

                  // 🔥 Merge isLoading state from conversations prop
                  // API 数据不包含 isLoading（它是客户端状态），需要从 conversations prop 合并
                  const uniqueStarredConversationsWithLoading =
                    uniqueStarredConversations.map(conv => {
                      const propsConv = conversations.find(
                        c => c.id === conv.id
                      );
                      return propsConv?.isLoading
                        ? { ...conv, isLoading: true }
                        : conv;
                    });

                  const hasStarredItems =
                    starredProjects.length > 0 ||
                    uniqueStarredConversations.length > 0;

                  if (!hasStarredItems) return null;

                  return (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <div
                          className="flex items-center gap-1 cursor-pointer select-none group"
                          onClick={() => {
                            const next = !isStarredExpanded;
                            setIsStarredExpanded(next);
                            localStorage.setItem(
                              'starredExpanded',
                              String(next)
                            );
                          }}
                        >
                          {isStarredExpanded ? (
                            <ChevronDown
                              size={14}
                              className="text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0"
                            />
                          ) : (
                            <ChevronRight
                              size={14}
                              className="text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0"
                            />
                          )}
                          <h3 className="text-xs font-medium text-gray-500 tracking-wide group-hover:text-gray-700 transition-colors">
                            {t('sidebar.starred')}
                          </h3>
                        </div>
                        {/* 🔥 Starred conversation 正在 loading 时显示 "Generating..." */}
                        {hasLoadingStarredConversations && (
                          <div className="flex items-center gap-1">
                            <div className="animate-spin rounded-full h-2.5 w-2.5 border-b border-gray-400"></div>
                            <span className="text-xs text-gray-400">
                              {t('chat.thinking')}
                            </span>
                          </div>
                        )}
                      </div>
                      {isStarredExpanded && (
                        <div className="mb-6">
                          {/* Starred Projects */}
                          {starredProjects.length > 0 && (
                            <ProjectList
                              projects={starredProjects}
                              currentProjectId={currentProjectId}
                              currentConversationId={currentConversationId}
                              shareConversationId={shareConversationId}
                              hasLoadingConversations={hasLoadingConversations}
                              isHighlighted={projectSelectedFrom === 'starred'}
                              hideConversations={true}
                              onProjectSelect={projectId => {
                                setProjectSelectedFrom('starred');
                                onProjectSelect(projectId);
                              }}
                              onConversationSelect={onConversationSelect}
                              onRenameProject={onRenameProject}
                              // onDeleteProject={projectId =>
                              //   onUpdateProjectConversation(projectId, 'DELETE')
                              // }
                              onUnstarProject={handleUnstarProject}
                              onRenameConversation={onRenameConversation}
                              onDeleteConversation={async id => {
                                const res = await onUpdateConversation(
                                  id,
                                  'DELETE'
                                );
                                return res;
                              }}
                              onShareConversation={handleShareConversation}
                              onBackToChat={onBackToChat}
                            />
                          )}
                          {/* Starred Conversations */}
                          {uniqueStarredConversationsWithLoading.length > 0 && (
                            <ConversationList
                              conversations={
                                uniqueStarredConversationsWithLoading
                              }
                              currentConversationId={currentConversationId}
                              shareConversationId={shareConversationId}
                              isLoading={false}
                              hasLoadingConversations={hasLoadingConversations}
                              projects={storeProjects}
                              onConversationSelect={onConversationSelect}
                              onRenameConversation={onRenameConversation}
                              onDeleteConversation={async id => {
                                const res = await onUpdateConversation(
                                  id,
                                  'DELETE'
                                );
                                return res;
                              }}
                              onShareConversation={handleShareConversation}
                              onSelectedNavItem={onSelectedNavItem}
                              onBackToChat={onBackToChat}
                              onToggleStar={handleToggleStar}
                            />
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}

              {/* Projects Section - Show when expanded */}
              {/* {!shareConversationId && isProjectsExpanded && (
          <>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-medium text-gray-500 tracking-wide">
                Projects
              </h3>
            </div>
            <div className="mb-6">
              {storeProjects.length === 0 ? (
                <div className="text-xs text-gray-400 text-center py-4">
                  No projects yet
                </div>
              ) : (
                <>
                  <ProjectList
                    projects={storeProjects.slice(0, 5)}
                    currentProjectId={currentProjectId}
                    currentConversationId={currentConversationId}
                    shareConversationId={shareConversationId}
                    hasLoadingConversations={hasLoadingConversations}
                    isHighlighted={projectSelectedFrom === 'projects'}
                    onProjectSelect={projectId => {
                      setProjectSelectedFrom('projects');
                      onProjectSelect(projectId);
                    }}
                    onConversationSelect={onConversationSelect}
                    onRenameProject={onRenameProject}
                    onDeleteProject={projectId =>
                      onUpdateProjectConversation(projectId, 'DELETE')
                    }
                    onRenameConversation={onRenameConversation}
                    onDeleteConversation={id =>
                      onUpdateConversation(id, 'DELETE')
                    }
                    onShareConversation={handleShareConversation}
                    onBackToChat={onBackToChat}
                  />
                  {storeProjects.length > 5 && (
                    <button
                      onClick={() => onShowProjectsList()}
                      className="w-full mt-3 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    >
                      Show all
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        )} */}
              {/* Recent Chats Header */}
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-medium text-gray-500 tracking-wide">
                  {t('sidebar.recentChats')}
                </h3>
                {/* 🔥 使用 hasLoadingRecentConversations，排除 Starred conversations 的 loading */}
                {(hasLoadingRecentConversations ||
                  isLoadingGeneralConversations) && (
                  <div className="flex items-center gap-1">
                    <div className="animate-spin rounded-full h-2.5 w-2.5 border-b border-gray-400"></div>
                    <span className="text-xs text-gray-400">
                      {hasLoadingRecentConversations
                        ? t('chat.thinking')
                        : t('common.loading')}
                    </span>
                  </div>
                )}
              </div>
              <div className="scrollbar-hide pb-4">
                <ConversationList
                  conversations={recentConversations.filter(c => {
                    if (c.starred) return false; // 已 starred 的在 Starred 区域显示
                    // 🔥 过滤掉 archived 项目的 conversation
                    if (c.projectId) {
                      const project = storeProjects.find(
                        p => p.id === c.projectId
                      );
                      if (project?.archived) return false;
                    }
                    return true;
                  })}
                  currentConversationId={currentConversationId}
                  shareConversationId={shareConversationId}
                  isLoading={
                    recentLoading || isLoadingGeneralConversations || false
                  }
                  hasLoadingConversations={hasLoadingRecentConversations}
                  projects={storeProjects}
                  onConversationSelect={onConversationSelect}
                  onRenameConversation={onRenameConversation}
                  onDeleteConversation={id =>
                    onUpdateConversation(id, 'DELETE')
                  }
                  onShareConversation={handleShareConversation}
                  onSelectedNavItem={onSelectedNavItem}
                  onBackToChat={onBackToChat}
                  onToggleStar={handleToggleStar}
                />
                {recentTotalCount > 30 && (
                  <div className="mt-1 px-2">
                    <button
                      onClick={() => {
                        onSelectedNavItem('chats');
                        onBackToChat();
                        // 🔥 移动端点击 All Chats 后收起侧边栏
                        if (window.innerWidth <= 767) {
                          useUIStore.getState().setIsSidebarCollapsed(true);
                        }
                      }}
                      className={`w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-600 hover:bg-[#e8e6dc] rounded-lg transition-all duration-200 ${
                        showAllChatsButton
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-0 translate-y-4 pointer-events-none'
                      }`}
                    >
                      <div className="p-0.5 rounded-full border border-gray-400">
                        <MoreHorizontal size={14} className="text-gray-600" />
                      </div>
                      <span>{t('sidebar.allChats')}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 pb-12"></div>
            {/* User Section at bottom - expanded */}
            <div className="absolute bottom-0 left-0 right-0 px-4 py-2 bg-gradient-to-t from-white via-white to-transparent shadow-[0_-8px_16px_rgba(0,0,0,0.04)] backdrop-blur-sm">
              {isLoggedIn ? (
                <UserSection
                  isLoggedIn={isLoggedIn}
                  userInfo={userInfo}
                  onLogin={onLogin}
                  onLogout={onLogout}
                />
              ) : (
                <UserSection
                  isLoggedIn={false}
                  userInfo={userInfo}
                  onLogin={onLogin}
                  onLogout={onLogout}
                />
              )}
            </div>{' '}
            {/* 闭合 UserSection 的绝对定位 div */}
          </>
        )}{' '}
        {/* 闭合展开状态的条件渲染 */}
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModal.isOpen}
        onClose={closeShareModal}
        conversationId={shareModal.conversationId}
        conversationTitle={shareModal.conversationTitle}
        projectId={shareModal.projectId}
      />

      {/* 拖拽调整宽度的手柄 - 参照 ArtifactViewer 设计 */}
      <div
        className="group w-2 absolute top-0 bottom-0 right-0 h-full cursor-col-resize -mr-1 z-20 grid place-items-center"
        onMouseDown={handleMouseDown}
        style={{ touchAction: 'none' }}
      >
        {/* 细线 */}
        <div
          className={`absolute top-0 bottom-0 left-1 w-[0.5px] transition-all ${
            isDragging
              ? 'bg-blue-500 w-px translate-x-[0.5px]'
              : 'bg-gray-300 group-hover:delay-75 group-hover:bg-blue-400 group-hover:w-px group-hover:translate-x-[0.5px]'
          }`}
        />
        {/* 圆形滑块指示器 */}
        {/* <div
          className={`h-6 w-2 relative rounded-full border bg-white shadow transition duration-200 cursor-col-resize ${
            isDragging
              ? 'border-blue-500 bg-blue-500'
              : 'border-gray-300 group-hover:delay-75 group-hover:border-blue-500 group-hover:bg-blue-500'
          }`}
        /> */}
      </div>
    </div>
  );
}
